import { IndexResponse } from '@/api/api-nika'
import { Listings_Detail, Floors, Virtual_Tours } from '@/types/cate-graphql-generated-types'
import {
  CardinalPoint,
  Kitchen,
  FloorPosition,
  FloorShift,
  CoolingSystem,
  StateOfMaintenance,
  StateAtDeed,
  Furnishings,
  EnergyRating,
} from '@/types/graphql-generated-types'
import { CasavoInventory } from '@/types/listing'
import {
  Listing as ListingResponse,
  Amenities,
  ListingSegment,
  ListingFloor as NikaFloor,
  ListingDescription as NikaDescription,
  ListingTitle as NikaTitle,
  ListingMediaItem as NikaMedia,
  GeoPOI,
} from '@/types/nika'

export type Maybe<T> = T | null

const KitchenMap = {
  full: Kitchen.Habitable,
  half: Kitchen.HalfHabitable,
  mini: Kitchen.Kitchenette,
  open: Kitchen.Open,
}

const FloorPositionMap = {
  basement: FloorPosition.Basement,
  ground: FloorPosition.GroundFloor,
  last: FloorPosition.LastFloor,
  mid: FloorPosition.MidFloor,
}

const FloorShiftMap = {
  lowered: FloorShift.Lowered,
  null: FloorShift.None,
  raised: FloorShift.Raised,
}

const CoolingSystemMap = {
  central: CoolingSystem.Shared,
  independent: CoolingSystem.Autonomous,
  none: CoolingSystem.None,
  ready: CoolingSystem.ReadyForInstallation,
}

const HeatingSystemMap = {
  central: CoolingSystem.Shared,
  independent: CoolingSystem.Autonomous,
  none: CoolingSystem.None,
}

const StateOfMaintenanceMap = {
  construction: StateOfMaintenance.UnderConstruction,
  habitable: StateOfMaintenance.Habitable,
  need_renovation: StateOfMaintenance.NeedRenovation,
  new: StateOfMaintenance.New,
  renovated: StateOfMaintenance.Renovated,
}

const StateAtDeedMap = {
  bare: StateAtDeed.BareOwnership,
  free: StateAtDeed.Free,
  occupied: StateAtDeed.Occupied,
  rented: StateAtDeed.Rented,
  unknown: StateAtDeed.Unknown,
}

const FurnishingsMap = {
  full: Furnishings.FullyFurnished,
  none: Furnishings.NotFurnished,
  partial: Furnishings.PartiallyFurnished,
}

const InventoryMap = {
  ibuyer: CasavoInventory.iBuyer,
  null: CasavoInventory.Third_Party,
  oda: CasavoInventory.Agency,
}

const EnergyRatingMap = {
  A: EnergyRating.A,
  A1: EnergyRating.A1,
  A2: EnergyRating.A2,
  A3: EnergyRating.A3,
  A4: EnergyRating.A4,
  APLUS: EnergyRating.Aplus,
  B: EnergyRating.B,
  C: EnergyRating.C,
  D: EnergyRating.D,
  E: EnergyRating.E,
  F: EnergyRating.F,
  G: EnergyRating.G,
  none: EnergyRating.None,
  pending: EnergyRating.PendingRequest,
}

function mapFloor(floor: NikaFloor) {
  return {
    buildingFloor: floor.building,
    entireFloor: floor.entire,
    floorShift: FloorShiftMap[floor.shift],
  } as Floors
}

function mapCountry(country: string) {
  return {
    es: 'spain',
    fr: 'france',
    it: 'italy',
    pt: 'portugal',
  }[country]
}

function mapDescription(description: NikaDescription) {
  return {
    language: description.language.toUpperCase(),
    neighbourhoodDescription: description.neighbourhood,
    propertyDescription: description.property,
  }
}

export function mapTitle(description: NikaTitle) {
  return {
    language: description.language,
    text: description.title,
  }
}

function hasSegment(segment: ListingSegment[]) {
  return segment && segment.length > 0
}

function mapCardinal(direction: string) {
  return direction.toUpperCase() as CardinalPoint
}

//TODO: check price euro vs eurocents
function mapFurnishingProposal(data: ListingResponse, x: NikaMedia) {
  return { price: data.pricing_sell_value, url: x.url }
}

function mapPhotos(x: NikaMedia) {
  return { isPrimary: x.is_primary, order: x.index, url: x.url }
}

/**
 *
 * @param date something like YYYY-MM-DD
 */
export function formatYear(date: string) {
  return date && parseInt(date.split('-')[0])
}

export function mapFloorplan(data: ListingResponse) {
  if (data?.media_floorplans?.[0]?.url) {
    return {
      url: data.media_floorplans[0].url,
    }
  }
  return {}
}

export function mapBox(nikaBox) {
  if (nikaBox.length === 0) return null

  return {
    area: null,
    areas: null,
    counter: nikaBox.length,
  }
}

export const mapPoi = (pois: GeoPOI[]) => {
  return pois.map((poi) => {
    return {
      coordinates: {
        lat: poi.geo_location.coordinates[1],
        lon: poi.geo_location.coordinates[0],
      },
      distance: poi.distance,
      name: poi.label,
      poiType: poi.type.toUpperCase(),
    }
  })
}

export const mapVirtualTours = (url: string): Virtual_Tours => {
  return url ? { url: url } : null
}

export function nikaListingsToLegacyListings(data: ListingResponse): Listings_Detail {
  return {
    additionalInfo: {
      condo: data.property_belongs_condo,
      // FIXME this one seem unused
      condoFeesPerYear: data.pricing_fees_condo_value * 100,
      condoFeesPerYearCate: {
        // MEMO pricing_fees_condo_value is €, condoFeesPerYearCate in euro cents
        cents: data.pricing_fees_condo_value * 100,
        currency: data.pricing_fees_condo_unit,
      },
      constructionYear: formatYear(data.property_date_construction),
      coolingSystem: CoolingSystemMap[data.property_system_cooling] as Maybe<CoolingSystem>,
      energyPerformanceIndex: data.administrative_energy_performance_index,
      energyRating: EnergyRatingMap[data.administrative_energy_rating.value],
      energyRatingCategory: data.administrative_energy_rating.system,
      estateRegistryArea: data.administrative_area_registry,
      estateRegistryClass: data.administrative_class_registry.toUpperCase(),
      exposure: data.property_exposures.map(mapCardinal),
      furnishings: FurnishingsMap[data.property_state_furniture] as Furnishings,
      glazing: (data.property_windows_glazing || 'single').toUpperCase(),
      heatingSystem: HeatingSystemMap[data.property_system_heating],
      kitchen: data.property_type_kitchen ? KitchenMap[data.property_type_kitchen] : (null as Maybe<Kitchen>),
      lastRenovationYear: formatYear(data.property_date_renovation),
      stateAtDeed: StateAtDeedMap[data.administrative_state_deed] || 'unknown',
      superCondo: false,
      views: data.property_views,
    },
    addressesCate: {
      street: data.geo_street,
      streetNumber: data.geo_street_number,
      zipCode: data.geo_zip,
    },
    characteristics: {
      balconies: { counter: data.property_segments_balcony.length },
      bathrooms: data.property_rooms_bath,
      bedrooms: data.property_rooms_bed,
      rooms: data.property_rooms_all,
      stateOfMaintenance: StateOfMaintenanceMap[data.property_state_maintenance] as StateOfMaintenance,
      terraces: { counter: data.property_segments_terrace.length },
    },
    contactInfo: {
      agencyAddress: data.contacts && data.contacts.address,
      agencyLogo: null,

      agencyName: data.contacts && data.contacts.name,
      ownedByCasavo: data.inventory != null,
      telephoneNumberContact: data.contacts && data.contacts.phone,
    },
    coordinates: {
      lat: data.geo_location.coordinates[1],
      lon: data.geo_location.coordinates[0],
    },
    country: mapCountry(data.geo_country),
    descriptions: data.descriptions.map(mapDescription),
    detail: {
      buildingFloors: data.property_floor_building,
      casavoDirectSale: data.sales_pipeline === 'direct',
      city: data.geo_city,
      commercialArea: data.administrative_area_raw,
      entireBuilding: data.property_is_entire_building,
      entranceFloor: data.property_floor_entrance,
      exclusiveSellingMandate: false, // not used
      floorPosition: [FloorPositionMap[data.property_floor_position] as FloorPosition],
      floors: data.property_floors.map(mapFloor),
      inventory: InventoryMap[data.inventory],
      privateNegotiation: data.pricing_sell_value === null,
      salesPipeline: data.sales_pipeline,
      sellingPrices: {
        cents: data.pricing_sell_value * 100,
        currency: data.pricing_sell_unit,
      },
      sellingUnitId: data.selling_unit_id,
      titles: data.titles.map(mapTitle),
      transactionId: data.transaction_id,
      zone: data.geo_zone ? { label: data.geo_labels.zone, slug: data.geo_zone } : null,
    },
    furnishingProposals:
      data.media_furnishing_proposals.length > 0
        ? data.media_furnishing_proposals.map((x) => mapFurnishingProposal(data, x))[0]
        : null,
    id: data.id,
    insertedAt: data.created_at.toString(), //remove this, as it's not used, after refactoring type
    photos: data.media_pictures.map(mapPhotos),
    planimetries: mapFloorplan(data),
    pointsOfInterest: mapPoi(data.geo_pois),
    publicUrls: null,
    publishedAt: data.created_at.toString(),
    ranking: 0,
    residentialType: data.property_type,
    services: {
      alarmSystem: data.amenities.includes(Amenities.alarm),
      armoredDoor: data.amenities.includes(Amenities.door_armor),
      basement: hasSegment(data.property_segments_basement),
      box: mapBox(data.property_segments_box),
      conciergeService: data.amenities.includes(Amenities.concierge),
      disabledAccess: data.amenities.includes(Amenities.barrier_access),
      disabledFriendly: data.amenities.includes(Amenities.barrier_friendly),
      electricGate: data.amenities.includes(Amenities.gate_electric),
      elevator: data.amenities.includes(Amenities.elevator),
      entranceVideoPhone: data.amenities.includes(Amenities.interphone_video),
      fastInternet: data.amenities.includes(Amenities.fiber),
      fireplace: data.amenities.includes(Amenities.fireplace),
      garden: data.property_feature_garden,
      gardenArea: data.property_segments_garden.map((x) => x.area).reduce((a, b) => a + b, 0),
      garret: hasSegment(data.property_segments_garret),
      homeAutomationSystem: data.amenities.includes(Amenities.home_automation),
      hydromassage: data.amenities.includes(Amenities.hydromassage),
      parkingSpaces: data.property_parking_spaces > 0,
      pool: data.property_feature_pool,
      recreationRoom: hasSegment(data.property_segments_recreative),
      satelliteSystem: data.amenities.includes(Amenities.tv_sat),
      sharedPool: data.amenities.includes(Amenities.shared_pool),
      tennisCourt: data.amenities.includes(Amenities.tennis_court),
      tvSystem: data.amenities.includes(Amenities.tv_antenna),
      walkingArea: data.administrative_area_net,
      wallCabinet: data.amenities.includes(Amenities.wall_cabinet),
    },
    showStreetNumber: data.geo_street_number != null,
    slug: data.id,
    updatedAt: data.updated_at,
    virtualTours: mapVirtualTours(data.url_tour_virtual),
  } as Listings_Detail
}

export function nikaMetaToLegacyMeta(data: IndexResponse<ListingResponse>, page: number) {
  if (data === undefined)
    return {
      hasMore: false,
      hasPrevious: false,
      inPage: 0,
      total: 0,
    }
  return {
    hasMore: data.meta.has_more,
    hasPrevious: page > 1,
    inPage: data.data.length,
    total: data.meta.total_objects,
  }
}
